import { Box, Flex, Text, useTheme, SimpleGrid } from "@chakra-ui/react";
import React from "react";
import { Theme } from "../../theme";
import { useNavigate } from "react-router-dom";
import getAxios from "../../hooks/useAxios";
import Cookies from "js-cookie";

interface IncentiveSummary {
  current_total: {
    amount: number;
    count: number;
  };
  current_cycle: {
    amount: number;
    count: number;
  };
  monthly: {
    month: string;
    amount: number;
    count: number;
  }[];
}

const Summary = () => {
  const theme = useTheme<Theme>();
  const navigate = useNavigate();
  const axios = getAxios();
  const access_token = Cookies.get("x-admin-auth");

  const [summary, setSummary] = React.useState<IncentiveSummary | null>(null);
  const [isDataFetching, setIsDataFetching] = React.useState(false);

  const getIncentivesSummary = async (url: string) => {
    setIsDataFetching(true);

    try {
      const res = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      });

      setSummary(res.data);
    } catch (error) {
      console.log("err", error);
    } finally {
      setIsDataFetching(false);
    }
  };

  React.useEffect(() => {
    getIncentivesSummary("/incentives/summary");
  }, []);

  const formatAmount = (amount: number) => {
    return `₹${amount?.toLocaleString("en-IN") || 0}`;
  };

  const formatMonth = (month: string) => {
    const [monthNumber, year] = month.split("-");

    const date = new Date(Number(year), Number(monthNumber) - 1, 1);

    return date.toLocaleString("en-IN", {
      month: "long",
      year: "numeric",
    });
  };

  return (
    <Box
      sx={{
        flex: 1,
        padding: "1rem",
        background: "#f7f7f7",
        minHeight: "100%",
      }}
    >
      {/* Header */}
      <Flex
        justifyContent="space-between"
        alignItems="center"
        marginBottom="1.5rem"
      >
        <Box>
          <Text
            fontSize="1.5rem"
            fontWeight="700"
            // color={theme.colors?.text?.primary || "#1A1A1A"}
          >
            Incentive Summary
          </Text>

          <Text
            fontSize="0.9rem"
            // color={theme.colors?.text?.secondary || "#666"}
            marginTop="4px"
          >
            Overview of incentive earnings and completed incentives
          </Text>
        </Box>
      </Flex>

      {/* Summary Cards */}
      <SimpleGrid
        columns={{
          base: 1,
          md: 2,
        }}
        spacing="1rem"
        marginBottom="1.5rem"
      >
        {/* Current Total */}
        <Box
          background="#fff"
          borderRadius="12px"
          padding="1.25rem"
          boxShadow="0px 2px 10px rgba(0,0,0,0.06)"
          border="1px solid #eee"
        >
          <Text
            fontSize="0.9rem"
            fontWeight="500"
            color="#777"
            marginBottom="0.75rem"
          >
            Current Total
          </Text>

          <Text
            fontSize="2rem"
            fontWeight="700"
            color={theme.colors?.primary || "#AD102D"}
          >
            {formatAmount(summary?.current_total?.amount || 0)}
          </Text>

          <Text fontSize="0.85rem" color="#777" marginTop="0.4rem">
            {summary?.current_total?.count || 0} incentives completed
          </Text>
        </Box>

        {/* Current Cycle */}
        <Box
          background="#fff"
          borderRadius="12px"
          padding="1.25rem"
          boxShadow="0px 2px 10px rgba(0,0,0,0.06)"
          border="1px solid #eee"
        >
          <Text
            fontSize="0.9rem"
            fontWeight="500"
            color="#777"
            marginBottom="0.75rem"
          >
            Current Cycle
          </Text>

          <Text
            fontSize="2rem"
            fontWeight="700"
            color={theme.colors?.primary || "#AD102D"}
          >
            {formatAmount(summary?.current_cycle?.amount || 0)}
          </Text>

          <Text fontSize="0.85rem" color="#777" marginTop="0.4rem">
            {summary?.current_cycle?.count || 0} incentives completed
          </Text>
        </Box>
      </SimpleGrid>

      {/* Monthly Incentives */}
      <Box
        background="#fff"
        borderRadius="12px"
        padding="1.25rem"
        boxShadow="0px 2px 10px rgba(0,0,0,0.06)"
        border="1px solid #eee"
      >
        <Flex
          justifyContent="space-between"
          alignItems="center"
          marginBottom="1rem"
        >
          <Box>
            <Text fontSize="1.1rem" fontWeight="600" color="#222">
              Monthly Incentives
            </Text>

            <Text fontSize="0.8rem" color="#888" marginTop="3px">
              Incentive earnings by month
            </Text>
          </Box>
        </Flex>

        {isDataFetching ? (
          <Box padding="2rem" textAlign="center">
            <Text color="#888">Loading...</Text>
          </Box>
        ) : summary?.monthly?.length ? (
          <Box>
            {/* Table Header */}
            <Flex
              padding="0.75rem 1rem"
              background="#f8f8f8"
              borderRadius="8px"
              marginBottom="0.5rem"
              fontSize="0.8rem"
              fontWeight="600"
              color="#777"
            >
              <Box flex="1">Month</Box>
              <Box width="150px">Incentives</Box>
              <Box width="150px" textAlign="right">
                Amount
              </Box>
            </Flex>

            {/* Monthly Rows */}
            {summary.monthly.map((item, index) => (
              <Flex
                key={`${item.month}-${index}`}
                alignItems="center"
                padding="1rem"
                borderBottom={
                  index !== summary.monthly.length - 1
                    ? "1px solid #eee"
                    : "none"
                }
              >
                <Box flex="1">
                  <Text fontSize="0.9rem" fontWeight="500" color="#333">
                    {formatMonth(item.month)}
                  </Text>
                </Box>

                <Box width="150px">
                  <Text fontSize="0.9rem" color="#555">
                    {item.count}
                  </Text>
                </Box>

                <Box width="150px" textAlign="right">
                  <Text
                    fontSize="0.95rem"
                    fontWeight="600"
                    color={theme.colors?.primary || "#AD102D"}
                  >
                    {formatAmount(item.amount)}
                  </Text>
                </Box>
              </Flex>
            ))}
          </Box>
        ) : (
          <Box padding="2rem" textAlign="center">
            <Text color="#888">No incentive data available</Text>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Summary;
